import styles from '../Id.module.scss'
import Link from 'next/link'
import CaseStudy from 'components/home/CaseStudy'
import { formatCreateDate } from 'lib/functions'
import { Post } from 'interfaces/posts'
import { useEffect, useState } from 'react'
import { gsap, Power4 } from 'gsap'
import Breadcrumb from 'components/commons/Breadcrumb/Breadcrumb'
import ActiveEvent from 'components/topics/ActiveEvent/ActiveEvent'
import { useRouter } from 'next/router'

import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import Button from 'components/commons/Button/Button'
import MailmagazineLink from 'components/mailmagazine/MailmagazineLink/MailmagazineLink'
gsap.registerPlugin(ScrollTrigger)

declare const hbspt: any

const statusToClassMap = {
  赤: 'statusIcon_red',
  赤枠: 'statusIcon_red_framed',
  青: 'statusIcon_blue',
  青枠: 'statusIcon_blue_framed',
  ピンク: 'statusIcon_pink',
  ピンク枠: 'statusIcon_pink_framed',
  オレンジ: 'statusIcon_orange',
  オレンジ枠: 'statusIcon_orange_framed',
  緑: 'statusIcon_green',
  緑枠: 'statusIcon_green_framed',
  紫: 'statusIcon_purple',
  紫枠: 'statusIcon_purple_framed',
  グレー: 'statusIcon_gray',
  グレー枠: 'statusIcon_gray_framed',
  薄グレー: 'statusIcon_light_gray',
  白: 'statusIcon_white',
}

const getStatusClass = (status) => {
  const baseClass = `${styles.statusIcon}`
  const specificClassName = statusToClassMap[status] || ''
  const specificClass = specificClassName ? styles[specificClassName] || '' : ''
  const fullClass = specificClass ? `${baseClass} ${specificClass}` : baseClass
  return fullClass.trim()
}

const getCategory = (categoryNM) => {
  const firstCategory = categoryNM.split(',')[0].trim()
  switch (firstCategory) {
    case 'ニュース':
      return 'news'
    case 'プレスリリース':
      return 'pressrelease'
    case 'イベント・セミナー':
      return 'events'
  }
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土']
  const dayOfWeek = daysOfWeek[date.getDay()]
  return {
    fullDate: `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${dayOfWeek}）`,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    dayOfWeek: dayOfWeek,
  }
}

const getEventDateDisplay = (post) => {
  const startInfo = post.start_date ? formatDate(post.start_date) : null
  const endInfo = post.end_date ? formatDate(post.end_date) : null

  if (startInfo && endInfo) {
    if (startInfo.fullDate === endInfo.fullDate) {
      return startInfo.fullDate
    } else if (startInfo.year === endInfo.year) {
      if (startInfo.month === endInfo.month) {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.day}日（${endInfo.dayOfWeek}）`
      } else {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.month}月${endInfo.day}日（${endInfo.dayOfWeek}）`
      }
    } else {
      return `${startInfo.fullDate}〜${endInfo.fullDate}`
    }
  } else {
    return startInfo?.fullDate || endInfo?.fullDate || ''
  }
}

const Id: React.FC<Post> = ({ post, prevPost, nextPost, categoryName, articleHtml, activeEvents }) => {
  const router = useRouter()
  const { page } = router.query

  const [breadcrumbPaths, setBreadcrumbPaths] = useState([
    { name: 'TOP', url: '/' },
    { name: 'Topics', url: page ? `/events/page/${page}` : '/events/page/1' },
  ])

  useEffect(() => {
    const elements = document.querySelectorAll(
      '.Topic_item00, .Topic_item01, .Topic_item02, .Topic_item03, .Topic_item04, .Topic_item05, .Topic_item06',
    )

    // 初期スタイルの設定
    gsap.set(elements, { opacity: 0, y: 50 })

    // アニメーションの設定
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: Power4.easeOut,
      stagger: 0.3, // 各要素のアニメーション開始を0.3秒ずつずらす
    })
  }, [])

  useEffect(() => {
    if (post && post.title && post.title.rendered) {
      const newBreadcrumb = { name: post.title.rendered, url: `/${categoryName}/${post.id}` }

      const previousPath = page ? `/events/page/${page}` : '/events/page/1'
      const updatedPaths = [{ name: 'TOP', url: '/' }, { name: 'Topics', url: previousPath }, newBreadcrumb]
      setBreadcrumbPaths(updatedPaths)
    }
  }, [post, page])

  useEffect(() => {
    if (document.querySelector('.hbspt-form')) {
      const hbsptformData = document
        .querySelector('.hbspt-form')
        .previousElementSibling.innerHTML.replace(/\n|hbspt.forms.create\({|}\);|\s/g, '')
        .split(',')
      setTimeout(() => {
        hbspt.forms.create({
          portalId: hbsptformData[0].replace(/.*:/, '').toString(),
          formId: hbsptformData[1].replace(/.*:|"/g, ''),
          target: hbsptformData[2].replace(/.*:|"/g, ''),
          region: hbsptformData[3].replace(/.*:|"/g, ''),
        })
      }, 100)
    }
  }, [post])

  const twitterShareUrl = `https://twitter.com/share?url=${encodeURIComponent(
    `https://www.i3design.jp/events/${post.id}`,
  )}&text=${encodeURIComponent(`${post.title.rendered} | i3DESIGN アイスリーデザイン`)}`
  const facebookShareUrl = `http://www.facebook.com/share.php?u=${encodeURIComponent(
    `https://www.i3design.jp/events/${post.id}`,
  )}`
  const hatenaBookmarkUrl = `http://b.hatena.ne.jp/add?mode=confirm&url=${encodeURIComponent(
    `https://www.i3design.jp/events/${post.id}`,
  )}&title=${encodeURIComponent(`${post.title.rendered} | i3DESIGN アイスリーデザイン`)}`
  const pocketShareUrl = `http://getpocket.com/edit?url=${encodeURIComponent(
    `https://www.i3design.jp/events/${post.id}`,
  )}&title=${encodeURIComponent(`${post.title.rendered} | i3DESIGN アイスリーデザイン`)}`

  return (
    <>
      <article>
        <section className={styles.section} key={post.id}>
          <div className={styles.breadcrumbs}>
            <Breadcrumb paths={breadcrumbPaths} />
          </div>
          <div className={styles.topicsFlex}>
            <div className={styles.wrapper}>
              <p className={`${styles.release} Topic_item00`}>{formatCreateDate(post.date)}</p>
              <h1 className={`${styles.topicItem01} Topic_item01`}>{post.title.rendered}</h1>
              {post.start_date && (
                <div className={`${styles.eventStatus} Topic_item02`}>
                  {post.status01 && <span className={getStatusClass(post.status01_color)}>{post.status01}</span>}
                  {post.status02 && <span className={getStatusClass(post.status02_color)}>{post.status02}</span>}
                  <span className={styles.day}>
                    {getEventDateDisplay(post)}
                    {post.duration && ` ${post.duration}`} 開催
                  </span>
                </div>
              )}
              <div
                className={`${styles.container} Topic_item03`}
                dangerouslySetInnerHTML={{ __html: articleHtml }}
              ></div>
              <div className={`${styles.share} Topic_item04`}>
                <p>この記事をシェアする</p>
                <div className={styles.share_items}>
                  <a
                    href={twitterShareUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_x}`}
                  ></a>
                  <a
                    href={facebookShareUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_fb}`}
                  ></a>
                  <a
                    href={hatenaBookmarkUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_hatena}`}
                  ></a>
                  <a
                    href={pocketShareUrl}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className={`${styles.sharebtn} ${styles.sharebtn_pocket}`}
                  ></a>
                </div>
              </div>
              <div className={`${styles.flex} Topic_item05`}>
                {prevPost ? (
                  <a href={`/${categoryName}/${prevPost.id}`} className={`${styles.link} ${styles.prevlink}`}>
                    <img src="/direction-reverse.svg" className={styles.prevDirection_img} alt={'Prev'} />
                    <div className={styles.is_pc}>
                      {prevPost.start_date && (
                        <div className={styles.link_date}>
                          <span>{getEventDateDisplay(prevPost)}</span>
                          <span>{prevPost.duration && ` ${prevPost.duration}`}</span>
                          <span>開催</span>
                        </div>
                      )}
                      <p className={styles.link_title}>{prevPost.title.rendered}</p>
                    </div>
                    <p className={styles.is_sp}>前の記事を見る</p>
                  </a>
                ) : (
                  <div></div>
                )}
                {nextPost ? (
                  <a href={`/${categoryName}/${nextPost.id}`} className={`${styles.link} ${styles.nextlink}`}>
                    <div className={styles.is_pc}>
                      {nextPost.start_date && (
                        <div className={styles.link_date}>
                          <span>{getEventDateDisplay(nextPost)}</span>
                          <span>{nextPost.duration && ` ${nextPost.duration}`}</span>
                          <span>開催</span>
                        </div>
                      )}
                      <p className={styles.link_title}>{nextPost.title.rendered}</p>
                    </div>
                    <p className={styles.is_sp}>次の記事を見る</p>
                    <img src="/direction_black.svg" className={styles.nextDirection_img} alt={'Next'} />
                  </a>
                ) : (
                  <div></div>
                )}
              </div>
              <Button text="一覧に戻る" href={`/pressrelease/page/${page || 1}`} />
            </div>
            <ActiveEvent activeEvents={activeEvents} getEventDateDisplay={getEventDateDisplay} />
          </div>
          <div className={styles.mailmagazine_link}>
            <p>
              UI/UXデザイン・システム内製化・DX推進についてのお役立ちメソッドや、限定イベントや最新事例などをお届けします。
            </p>
            <div className={styles.mailmagazine_link_button}>
              <Button text="メールマガジン" href="/mailmagazine" red="red" />
            </div>
          </div>
        </section>
        <CaseStudy tagType="h2" />
        <div className={styles.linklist}>
          <MailmagazineLink />
        </div>
      </article>
    </>
  )
}

export default Id
